import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { buildingLicenseApi, licensePlanApi, enumsApi } from '../services/api';
import { showError } from '../utils/showError';
import { extractErrorMessage } from '../utils/errorUtils';
import {
  PageHeader,
  SearchBar,
  LoadingSpinner,
  EmptyState,
  ListContainer,
  Pagination,
  ErrorMessage,
  SuccessMessage,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Modal,
  FormField,
  FormSelect,
  FormTextarea,
  ConfirmationModal,
  Badge,
} from '../components/common';
import { Shield, Plus, Pause, Play, CalendarPlus, XCircle, Eye } from 'lucide-react';
import {
  getLicenseStatusName,
  getLicenseStatusColor,
  getEnumDisplayName,
  formatEnumForSelect,
} from '../constants/licenses';

const BuildingLicenses = () => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchString, setSearchString] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);

  // Assign modal
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignForm, setAssignForm] = useState({
    buildingId: '',
    licensePlanId: '',
    status: 0,
    expiryDate: '',
    notes: '',
  });
  const [assignErrors, setAssignErrors] = useState({});
  const [assigning, setAssigning] = useState(false);

  // Extend modal
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extendLicenseId, setExtendLicenseId] = useState(null);
  const [extendDate, setExtendDate] = useState('');
  const [extending, setExtending] = useState(false);

  // Confirmation modal for status changes
  const [confirmModal, setConfirmModal] = useState({ show: false, action: null, licenseId: null, title: '', message: '' });

  // Plans dropdown data
  const [planOptions, setPlanOptions] = useState([]);
  const [activationMethodsRaw, setActivationMethodsRaw] = useState([]);

  // Fetch plans and enums on mount
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [plansRes, methodsRes] = await Promise.all([
          licensePlanApi.list({ pageSize: 100 }),
          enumsApi.getActivationMethods(),
        ]);
        const items = plansRes.data.items || plansRes.data || [];
        setPlanOptions(items.map((p) => ({ value: p.licensePlanId, label: `${p.name} (${p.planCode})` })));
        setActivationMethodsRaw(methodsRes.data || []);
      } catch (err) {
        console.error('Error fetching dropdown data:', err);
      }
    };
    fetchDropdownData();
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchString(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchLicenses = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        pageNumber: currentPage,
        pageSize,
        searchString: searchString || undefined,
      };
      const response = await buildingLicenseApi.list(params);
      setLicenses(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalCount(response.data.totalCount || 0);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLicenses();
  }, [currentPage, searchString]);

  const handleSearchChange = (value) => {
    setSearchInput(value);
  };

  // Assign modal
  const openAssignModal = () => {
    setAssignForm({ buildingId: '', licensePlanId: '', status: 0, expiryDate: '', notes: '' });
    setAssignErrors({});
    setShowAssignModal(true);
  };

  const validateAssignForm = () => {
    const errors = {};
    if (!assignForm.buildingId.trim()) errors.buildingId = 'Building ID is required';
    if (!assignForm.licensePlanId) errors.licensePlanId = 'Plan is required';
    setAssignErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAssign = async () => {
    if (!validateAssignForm()) return;
    setAssigning(true);
    try {
      const payload = {
        buildingId: assignForm.buildingId.trim(),
        licensePlanId: assignForm.licensePlanId,
        status: parseInt(assignForm.status),
        expiryDate: assignForm.expiryDate || null,
        notes: assignForm.notes.trim() || null,
      };
      await buildingLicenseApi.assign(payload);
      setShowAssignModal(false);
      setSuccessMessage('License assigned to building successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchLicenses();
    } catch (err) {
      showError(err);
    } finally {
      setAssigning(false);
    }
  };

  // Status actions
  const handleStatusAction = (action, licenseId, buildingName) => {
    const messages = {
      suspend: {
        title: 'Suspend License',
        message: `Are you sure you want to suspend the license for "${buildingName}"? The building will lose access to licensed features.`,
      },
      reactivate: {
        title: 'Reactivate License',
        message: `Are you sure you want to reactivate the license for "${buildingName}"?`,
      },
      cancel: {
        title: 'Cancel License',
        message: `Are you sure you want to cancel the license for "${buildingName}"? This action is permanent.`,
      },
    };

    setConfirmModal({
      show: true,
      action,
      licenseId,
      title: messages[action].title,
      message: messages[action].message,
    });
  };

  const executeStatusAction = async () => {
    const { action, licenseId } = confirmModal;
    try {
      if (action === 'suspend') await buildingLicenseApi.suspend(licenseId);
      else if (action === 'reactivate') await buildingLicenseApi.reactivate(licenseId);
      else if (action === 'cancel') await buildingLicenseApi.cancel(licenseId);

      setConfirmModal({ show: false, action: null, licenseId: null, title: '', message: '' });
      setSuccessMessage(`License ${action === 'reactivate' ? 'reactivated' : action + 'led'} successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchLicenses();
    } catch (err) {
      showError(err);
      setConfirmModal({ show: false, action: null, licenseId: null, title: '', message: '' });
    }
  };

  // Extend modal
  const openExtendModal = (licenseId) => {
    setExtendLicenseId(licenseId);
    setExtendDate('');
    setShowExtendModal(true);
  };

  const handleExtend = async () => {
    if (!extendDate) {
      setError('Please select a new expiry date');
      return;
    }
    setExtending(true);
    try {
      await buildingLicenseApi.extend(extendLicenseId, { newExpiryDate: extendDate });
      setShowExtendModal(false);
      setSuccessMessage('License extended successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchLicenses();
    } catch (err) {
      showError(err);
    } finally {
      setExtending(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Determine available actions based on license status
  const getActions = (license) => {
    const status = license.status;
    const actions = [];

    // Active (0), Trial (1), GracePeriod (2)
    if (status === 0 || status === 1 || status === 2) {
      actions.push({ key: 'suspend', icon: Pause, label: 'Suspend', color: 'text-yellow-600 hover:text-yellow-700' });
      actions.push({ key: 'extend', icon: CalendarPlus, label: 'Extend', color: 'text-blue-600 hover:text-blue-700' });
      actions.push({ key: 'cancel', icon: XCircle, label: 'Cancel', color: 'text-red-600 hover:text-red-700' });
    }
    // Suspended (3)
    if (status === 3) {
      actions.push({ key: 'reactivate', icon: Play, label: 'Reactivate', color: 'text-green-600 hover:text-green-700' });
      actions.push({ key: 'cancel', icon: XCircle, label: 'Cancel', color: 'text-red-600 hover:text-red-700' });
    }
    // Expired (4), Cancelled (5) — no actions

    return actions;
  };

  if (loading && licenses.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Building Licenses"
        subtitle={`Manage per-building license assignments (${totalCount} total)`}
        icon={Shield}
        action={
          <Button variant="primary" onClick={openAssignModal}>
            <Plus size={16} className="mr-2" />
            Assign License
          </Button>
        }
      />

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}
      {successMessage && <SuccessMessage message={successMessage} onDismiss={() => setSuccessMessage(null)} />}

      <div className="mb-6">
        <SearchBar
          value={searchInput}
          onChange={handleSearchChange}
          placeholder="Search by building name..."
        />
      </div>

      <ListContainer>
        {licenses.length === 0 ? (
          <EmptyState
            icon={Shield}
            title="No building licenses found"
            description={searchInput ? 'Try adjusting your search' : 'Get started by assigning a license to a building'}
            action={
              <Button variant="primary" onClick={openAssignModal}>
                <Plus size={16} className="mr-2" />
                Assign License
              </Button>
            }
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell header>Building</TableCell>
                  <TableCell header>Plan</TableCell>
                  <TableCell header>Status</TableCell>
                  <TableCell header>Start Date</TableCell>
                  <TableCell header>Expiry Date</TableCell>
                  <TableCell header>Activation</TableCell>
                  <TableCell header>Actions</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {licenses.map((license) => {
                  const actions = getActions(license);
                  return (
                    <TableRow key={license.buildingLicenseId}>
                      <TableCell>
                        <div>
                          <span className="font-medium text-gray-900">{license.buildingName || 'Unknown'}</span>
                          <p className="text-xs text-gray-500 font-mono truncate max-w-[180px]">{license.buildingId}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-medium text-gray-900">
                          {license.planName || license.licensePlanName || 'N/A'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLicenseStatusColor(license.status)}`}>
                          {getLicenseStatusName(license.status)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-700">{formatDate(license.startDate || license.activatedAt)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-700">{formatDate(license.expiryDate || license.expiresAt)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-gray-600">
                          {getEnumDisplayName(license.activationMethod, activationMethodsRaw)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/license?buildingId=${license.buildingId}`}
                            className="transition-colors text-primary-600 hover:text-primary-700"
                            title="View license"
                          >
                            <Eye size={16} />
                          </Link>
                          {actions.map((action) => (
                            <button
                              key={action.key}
                              onClick={() => {
                                if (action.key === 'extend') {
                                  openExtendModal(license.buildingLicenseId);
                                } else {
                                  handleStatusAction(action.key, license.buildingLicenseId, license.buildingName || 'this building');
                                }
                              }}
                              className={`transition-colors ${action.color}`}
                              title={action.label}
                            >
                              <action.icon size={16} />
                            </button>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  totalItems={totalCount}
                  itemsPerPage={pageSize}
                />
              </div>
            )}
          </>
        )}
      </ListContainer>

      {/* Assign License Modal */}
      <Modal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        title="Assign License to Building"
        size="medium"
      >
        <div className="space-y-4">
          <FormField
            label="Building ID"
            name="buildingId"
            value={assignForm.buildingId}
            onChange={(e) => setAssignForm({ ...assignForm, buildingId: e.target.value })}
            required
            error={assignErrors.buildingId}
            placeholder="Enter building GUID"
          />

          <FormSelect
            label="License Plan"
            name="licensePlanId"
            value={assignForm.licensePlanId}
            onChange={(e) => setAssignForm({ ...assignForm, licensePlanId: e.target.value })}
            required
            error={assignErrors.licensePlanId}
            options={[
              { value: '', label: 'Select a plan...' },
              ...planOptions,
            ]}
          />

          <FormSelect
            label="Initial Status"
            name="status"
            value={assignForm.status}
            onChange={(e) => setAssignForm({ ...assignForm, status: e.target.value })}
            options={[
              { value: 0, label: 'Active' },
              { value: 1, label: 'Trial' },
            ]}
          />

          <FormField
            label="Expiry Date"
            name="expiryDate"
            type="date"
            value={assignForm.expiryDate}
            onChange={(e) => setAssignForm({ ...assignForm, expiryDate: e.target.value })}
            helpText="Leave empty to use plan default expiration"
          />

          <FormTextarea
            label="Notes"
            name="notes"
            value={assignForm.notes}
            onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
            rows={2}
            placeholder="Optional notes..."
          />

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button variant="secondary" onClick={() => setShowAssignModal(false)} disabled={assigning}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAssign} loading={assigning}>
              Assign License
            </Button>
          </div>
        </div>
      </Modal>

      {/* Extend License Modal */}
      <Modal
        isOpen={showExtendModal}
        onClose={() => setShowExtendModal(false)}
        title="Extend License"
        size="small"
      >
        <div className="space-y-4">
          <FormField
            label="New Expiry Date"
            name="extendDate"
            type="date"
            value={extendDate}
            onChange={(e) => setExtendDate(e.target.value)}
            required
            helpText="Select a new expiry date for this license"
          />

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button variant="secondary" onClick={() => setShowExtendModal(false)} disabled={extending}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleExtend} loading={extending}>
              Extend
            </Button>
          </div>
        </div>
      </Modal>

      {/* Status Action Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.show}
        onClose={() => setConfirmModal({ show: false, action: null, licenseId: null, title: '', message: '' })}
        onConfirm={executeStatusAction}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.action === 'cancel' ? 'Cancel License' : confirmModal.action === 'suspend' ? 'Suspend' : 'Reactivate'}
        confirmVariant={confirmModal.action === 'reactivate' ? 'primary' : 'danger'}
      />
    </div>
  );
};

export default BuildingLicenses;
